import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import FBTrackingService from "@/services/fbTrackingService";
import SubscriptionServices from "@/services/subscriptionServices";
import { safeOpenURL } from "@/utils/linking";
import { Ionicons } from "@expo/vector-icons";
import { useQuery } from "@tanstack/react-query";
import { useLocalSearchParams } from "expo-router";
import React, { useEffect } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const SubscriptionDetailScreen = () => {
  const { id, name } = useLocalSearchParams();
  const { data, isLoading } = useQuery({
    queryKey: ["subscription", "detail", id],
    queryFn: () => {
      return SubscriptionServices.getCancelGuide(id as string);
    },
    enabled: !!id,
  });

  useEffect(() => {
    FBTrackingService.trackViewContent("subscription-detail");
  }, []);

  const guideFound = data?.success && data?.data;
  const { merchantName, steps, details } = data?.data || {};

  const actionItems = [
    {
      icon: "mail-outline",
      title: "Contact the service",
      url: details?.contact,
      onPress: () => {
        safeOpenURL(details?.contact);
      },
    },
    {
      icon: "globe-outline",
      title: "Visit the website",
      url: details?.website,
      onPress: () => {
        safeOpenURL(details?.website);
      },
    },
  ].filter((item) => item.url && item.url.trim() !== '');

  const cancellationSteps = steps;

  const displayName = merchantName || (name as string) || "Subscription";

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title={displayName} />
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!guideFound) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title={displayName} />
        <View style={styles.content}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>No cancellation guide available</Text>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header title={displayName} />

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            Cancel the subscription yourself
          </Text>

          <View style={styles.actionsList}>
            {actionItems.map((item, index) => (
              <TouchableOpacity
                key={index}
                style={styles.actionItem}
                onPress={item.onPress}
              >
                <View style={styles.actionLeft}>
                  <View style={styles.iconContainer}>
                    <Ionicons
                      name={item.icon as any}
                      size={20}
                      color="#FFFFFF"
                    />
                  </View>
                  <Text style={styles.actionTitle}>{item.title}</Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color="#9CA3AF" />
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cancellation instructions</Text>

          <View style={styles.instructionsList}>
            {cancellationSteps?.map((step, index) => (
              <View key={index} style={styles.instructionItem}>
                <Text style={styles.stepNumber}>{index + 1}.</Text>
                <Text style={styles.stepText}>{step}</Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default SubscriptionDetailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    fontSize: 16,
    color: "#6B7280",
  },
  section: {
    marginTop: 24,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#1F2937",
    marginBottom: 20,
    lineHeight: 32,
  },
  actionsList: {
    gap: 12,
  },
  actionItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  actionLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: "500",
    color: "#1F2937",
  },
  instructionsList: {
    gap: 16,
  },
  instructionItem: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  stepNumber: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1F2937",
    marginRight: 8,
    minWidth: 20,
  },
  stepText: {
    fontSize: 16,
    color: "#1F2937",
    lineHeight: 24,
    flexGrow: 1,
  },
});
